import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useConfirm } from '../components/ConfirmDialog';
import { RegularisationPanel } from '../components/RegularisationPanel';
import { SystemHealthPanel } from '../components/SystemHealthPanel';
import { RattachementPanel } from '../components/RattachementAnomalies';
import { ShieldCheck, Loader2, RefreshCw, AlertTriangle, CheckCircle2, Merge, Download } from 'lucide-react';

const LABELS = {
    vehicule_commercial_autre_agence: 'Véhicules dont le commercial est dans une autre agence',
    frais_vehicule_autre_agence: 'Frais rattachés à un véhicule d\'une autre agence',
    frais_vers_vehicule_supprime: 'Frais pointant vers un véhicule supprimé',
    frais_rattache_absent_du_dossier: 'Frais « rattachés » absents des frais du dossier',
    facture_vehicule_autre_agence: 'Factures reconnues sur un véhicule d\'une autre agence',
    facture_vers_vehicule_supprime: 'Factures pointant vers un véhicule supprimé',
    facture_vers_frais_supprime: 'Factures pointant vers un frais supprimé',
    frais_rattache_sans_justificatif_visible: 'Frais rattachés sans justificatif visible',
    factures_scannees_plusieurs_fois: 'Factures scannées plusieurs fois',
};

function Check({ k, v }) {
    const [open, setOpen] = useState(false);
    const ok = v.count === 0;
    return (
        <div className="rounded-xl border border-gray-200 bg-white p-4" data-testid={`coh-${k}`}>
            <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 text-left">
                {ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" /> : <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />}
                <span className="text-sm text-gray-900 flex-1">{LABELS[k] || k}</span>
                <span className={`text-sm font-semibold ${ok ? 'text-emerald-700' : 'text-amber-700'}`} data-testid={`coh-count-${k}`}>{v.count}</span>
            </button>
            {open && v.count > 0 && (
                <pre className="mt-3 text-[11px] bg-gray-50 rounded-lg p-3 overflow-x-auto text-gray-700 max-h-64">{JSON.stringify(v.exemples, null, 1)}</pre>
            )}
        </div>
    );
}

const COLL = { users: 'utilisateurs', vehicles: 'véhicules', frais_vehicules: 'frais véhicules', scan_documents: 'justificatifs', scan_results: 'factures scannées', charges: 'charges',
    frais_mutualises: 'frais mutualisés', sav_dossiers: 'dossiers SAV', vehicle_documents: 'documents véhicule', commission_history: 'historique commissions', paiements_commissions: 'paiements commissions',
    frais_bureau: 'frais de bureau', ecritures_commerciales: 'écritures commerciales', bank_accounts: 'comptes bancaires', bank_imports: 'imports bancaires', bank_operations: 'opérations bancaires',
    ia_analyses: 'analyses IA', controles_cg: 'contrôles CG', objectives: 'objectifs', closed_months: 'mois clôturés', tva_periodes: 'périodes TVA', tva_credits: 'crédits TVA', commission_settings: 'réglages commission' };
const STATUS = { disabled: 'désactivée', pending: 'en attente', active: 'active' };
const val = (v) => (v === null || v === undefined ? 'aucun' : String(v));
const REPAIRS = { frais_vers_vehicule_autre_agence: 'Frais rattachés à un véhicule de l\'autre fiche', factures_vers_vehicule_autre_agence: 'Factures reconnues sur un véhicule de l\'autre fiche', vehicules_commercial_autre_agence: 'Véhicules dont le commercial est sur l\'autre fiche' };
const eur = (n) => Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function Issues({ items, tone, testid }) {
    if (!items?.length) return null;
    const cls = tone === 'red' ? 'bg-red-50 border-red-200 text-red-900' : 'bg-amber-50 border-amber-200 text-amber-900';
    return (
        <div className={`rounded-lg border p-3 space-y-2 ${cls}`} data-testid={testid}>
            {items.map(i => (
                <div key={i.code} className="text-xs" data-testid={`${testid}-${i.code}`}>
                    <p className="font-semibold">{i.message}</p>
                    {Array.isArray(i.details) && i.details.length > 0 && typeof i.details[0] === 'object'
                        ? <ul className="mt-1 space-y-0.5">{i.details.map(d => <li key={d.reglage}>{d.reglage} : agence archivée <b>{val(d.source)}</b> / agence conservée <b>{val(d.cible)}</b></li>)}</ul>
                        : <p className="mt-1 break-words">{Array.isArray(i.details) ? i.details.join(', ') : typeof i.details === 'object' ? Object.entries(i.details).map(([k, v]) => `${COLL[k] || k} ${v}`).join(', ') : (STATUS[i.details] || i.details)}</p>}
                </div>
            ))}
        </div>
    );
}

function VehicleCensus({ v }) {
    const line = (k, label) => <li data-testid={`merge-veh-${k}`}>{label} : <b>{v[k].total}</b> ({Object.entries(v[k].par_statut).map(([s, n]) => `${s} ${n}`).join(', ') || 'aucun'})</li>;
    return (
        <div data-testid="merge-vehicles">
            <p className="text-xs font-semibold text-gray-800 mb-1">Véhicules (tous statuts) — aucun véhicule n'est supprimé</p>
            <ul className="text-xs text-gray-800 space-y-0.5">
                {line('cible', 'Agence conservée')}
                {line('source', 'Agence archivée')}
                <li>Total avant : <b data-testid="merge-veh-total">{v.total_avant}</b> · attendu après : <b>{v.total_attendu_apres}</b> (la réunion s'annule seule si un dossier manque)</li>
                <li>Dossiers uniques (par immatriculation) : <b data-testid="merge-veh-unique">{v.dossiers_uniques}</b></li>
                {v.plaques_en_double.map(d => <li key={d.plaque} className="text-amber-800">Doublon {d.fiches[0].immatriculation} : {d.fiches.length} fiches ({d.fiches.map(f => `${f.agence === 'cible' ? 'conservée' : 'archivée'} ${f.statut || ''}`).join(' / ')}) — conservées toutes les deux</li>)}
            </ul>
        </div>
    );
}

function MergePlan({ plan, confirm, setConfirm, run, busy }) {
    const t = plan.totaux_financiers || {};
    return (
        <div className="rounded-lg bg-gray-50 p-3 space-y-3" data-testid="merge-plan">
            <p className="text-xs text-gray-700">Simulation : <b>{plan.source.nom}</b> (…{plan.source.id.slice(-6)}) → <b>{plan.target.nom}</b> (…{plan.target.id.slice(-6)}). Rien n'a été modifié.</p>
            <div>
                <p className="text-xs font-semibold text-gray-800 mb-1">Enregistrements qui changeront d'agence</p>
                <ul className="text-xs text-gray-800 grid grid-cols-2 gap-1">{Object.entries(plan.a_deplacer).filter(([, n]) => n > 0).map(([c, n]) => <li key={c} data-testid={`merge-move-${c}`}>{COLL[c] || c} : <b>{n}</b></li>)}</ul>
            </div>
            <div>
                <p className="text-xs font-semibold text-gray-800 mb-1">Liens qui redeviennent cohérents</p>
                <ul className="text-xs text-gray-800 space-y-0.5">{Object.entries(plan.liens_repares || {}).map(([k, n]) => <li key={k} data-testid={`merge-repair-${k}`}>{REPAIRS[k] || k} : <b>{n}</b></li>)}</ul>
            </div>
            {plan.vehicules && <VehicleCensus v={plan.vehicules} />}
            {plan.gel_frais_mutualises?.length > 0 && (
                <div data-testid="merge-gel">
                    <p className="text-xs font-semibold text-gray-800 mb-1">Frais mutualisés figés (marges et commissions historiques inchangées)</p>
                    <ul className="text-xs text-gray-800 space-y-0.5">{plan.gel_frais_mutualises.map(g => <li key={g.mois} data-testid={`merge-gel-${g.mois}`}>{g.mois} : {g.nb_frais} frais — quote-part figée {eur(g.quote_part_figee)} €{g.non_repartis ? ' (non répartis, à traiter séparément)' : ''}</li>)}</ul>
                    <p className="text-[11px] text-gray-500 mt-1">Les mois suivants restent répartis normalement. La réunion s'annule seule si une marge ou une commission change.</p>
                </div>
            )}
            <div data-testid="merge-totals">
                <p className="text-xs font-semibold text-gray-800 mb-1">Montants des deux fiches (inchangés après réunion, vérifiés automatiquement)</p>
                <ul className="text-xs text-gray-800 space-y-0.5">
                    {t.vehicles && <li>Véhicules : achats {eur(t.vehicles.prix_achat)} € · ventes {eur(t.vehicles.prix_vente)} € · frais des dossiers {eur(t.vehicles.frais_dossiers)} €</li>}
                    {t.frais_vehicules && <li>Frais véhicules : {eur(t.frais_vehicules.montant_ttc)} € TTC</li>}
                    {t.charges && <li>Charges : {eur(t.charges.montant_ttc ?? t.charges.montant)} €</li>}
                </ul>
            </div>
            <p className="text-xs text-gray-600" data-testid="merge-backup">Sauvegarde complète avant réunion : {plan.sauvegarde_documents} document(s) copiés intégralement. Annulation possible à tout moment. Aucun montant, aucune dépense créée.</p>
            <Issues items={plan.conflits_bloquants} tone="red" testid="merge-blocking" />
            <Issues items={plan.avertissements} tone="amber" testid="merge-warnings" />
            {plan.executable ? (
                <>
                    <Input value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Tapez REUNIR pour confirmer" className="h-9 text-sm" data-testid="merge-confirm-input" />
                    <Button size="sm" onClick={run} disabled={busy || confirm !== 'REUNIR'} className="h-8 text-xs bg-red-600 hover:bg-red-700 text-white" data-testid="merge-run">Réunir maintenant</Button>
                </>
            ) : <p className="text-xs font-semibold text-red-700" data-testid="merge-blocked">Réunion bloquée tant que les conflits ci-dessus ne sont pas réglés.</p>}
        </div>
    );
}

function MergeHistory({ merges, overview, onDone }) {
    const [confirm, confirmDialog] = useConfirm();
    if (!merges.length) return null;
    const nm = (id) => { const a = overview.find(x => x.id === id); return a ? `${a.nom.trim()} (…${id.slice(-4)})` : `…${id.slice(-6)}`; };
    const rollback = async (id) => {
        if (!(await confirm('Annuler cette réunion et remettre chaque enregistrement dans son agence d\'origine ?', { title: 'Annuler la réunion', confirmLabel: 'Annuler la réunion', danger: true }))) return;
        try { await axios.post(`${API_URL}/api/admin/agencies/merge/${id}/rollback`, {}, { withCredentials: true }); toast.success('Réunion annulée'); onDone(); }
        catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    };
    return (
        <div className="rounded-2xl border border-gray-200 bg-white p-5 space-y-2" data-testid="merge-history">
            {confirmDialog}
            <p className="text-sm font-semibold text-gray-900">Historique des réunions</p>
            {merges.map(m => (
                <div key={m.id} className="flex flex-wrap items-center gap-2 text-xs text-gray-700 border-t border-gray-100 pt-2">
                    <span className="flex-1 min-w-0 break-words">{new Date(m.at).toLocaleString('fr-FR')} · {nm(m.source)} → {nm(m.target)} · {Object.entries(m.moved || {}).map(([c, n]) => `${COLL[c] || c} ${n}`).join(', ')} · <b>{m.status === 'annule' ? 'annulée' : 'effectuée'}</b>
                        {m.verification && <span className="block text-emerald-700" data-testid={`merge-verification-${m.id}`}>Vérification : véhicules {m.verification.vehicules_avant ?? '?'} avant / {m.verification.vehicules_apres ?? '?'} après · documents identiques {m.verification.documents_identiques ? 'oui' : 'non'} · montants identiques {m.verification.totaux_identiques ? 'oui' : 'non'} · restant dans l'agence archivée {m.verification.reste_dans_source}</span>}</span>
                    {m.status === 'termine' && <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => rollback(m.id)} data-testid={`merge-rollback-${m.id}`}>Annuler</Button>}
                </div>
            ))}
        </div>
    );
}

function BackupVerif({ v }) {
    const yes = (b) => (b ? 'oui' : 'non');
    const rt = v.restauration_test || {};
    return (
        <ul className="text-xs text-gray-700 space-y-0.5" data-testid="backup-verification">
            <li>Véhicules sauvegardés : <b data-testid="backup-vehicles">{v.vehicules}</b> · justificatifs : <b>{v.fichiers}</b>{v.fichiers_manquants ? <span className="text-red-600"> · {v.fichiers_manquants} fichier(s) introuvable(s)</span> : ''}</li>
            <li>Archive intacte {yes(v.archive_intacte)} · base relue identique {yes(v.base_identique && v.comptes_identiques)} · fichiers identiques {yes(v.fichiers_identiques)}</li>
            <li data-testid="backup-restore-test">Restauration d'essai : {rt.ok === true ? 'réussie (base temporaire rechargée puis supprimée)' : rt.ok === false ? 'écarts détectés' : (rt.erreur || 'non testée')}</li>
        </ul>
    );
}

const STEP_LABELS = { base: 'Lecture de la base', fichiers: 'Récupération des justificatifs', archive: "Création de l'archive", verification: 'Vérification et empreinte', restauration: "Restauration d'essai", stockage: 'Copie de secours' };
const STEP_STYLE = { ok: 'text-emerald-700', en_cours: 'text-[#6366F1] font-semibold', echec: 'text-red-600 font-semibold', non_teste: 'text-amber-700', ecart: 'text-red-600' };
const STEP_TXT = { ok: 'terminé', en_cours: 'en cours…', echec: 'échec', non_teste: 'non effectué', ecart: 'écarts' };

function BackupSteps({ job }) {
    const steps = job.etapes || {};
    return (
        <ol className="text-xs space-y-0.5" data-testid="backup-steps">
            {Object.keys(STEP_LABELS).map((k, i) => {
                const st = steps[k]?.statut;
                const extra = k === 'fichiers' && job.fichiers_total != null ? ` ${job.fichiers_faits || 0}/${job.fichiers_total} traités${job.fichiers_erreurs ? ` · ${job.fichiers_erreurs} manquant(s)` : ''}` : '';
                return <li key={k} className={STEP_STYLE[st] || 'text-gray-400'} data-testid={`backup-step-${k}`}>{i + 1}. {STEP_LABELS[k]}{extra} — {STEP_TXT[st] || 'à venir'}{steps[k]?.detail ? ` (${steps[k].detail})` : ''}</li>;
            })}
        </ol>
    );
}

function BackupExport() {
    const [jobs, setJobs] = useState([]);
    const load = useCallback(() => axios.get(`${API_URL}/api/admin/backup/jobs`, { withCredentials: true }).then(r => setJobs(r.data)).catch(() => {}), []);
    useEffect(() => { load(); }, [load]);
    const last = jobs[0];
    const running = last?.statut === 'en_cours';
    const ready = jobs.find(x => x.statut === 'pret');
    useEffect(() => { if (!running) return undefined; const t = setInterval(load, 3000); return () => clearInterval(t); }, [running, load]);
    const start = async () => {
        try { await axios.post(`${API_URL}/api/admin/backup/start`, {}, { withCredentials: true }); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || 'Sauvegarde impossible'); }
    };
    const download = () => {
        const a = document.createElement('a'); a.href = `${API_URL}/api/admin/backup/jobs/${ready.id}/download`; a.download = ready.nom || 'sauvegarde.zip';
        document.body.appendChild(a); a.click(); a.remove();
    };
    return (
        <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-2" data-testid="backup-export">
            <p className="text-sm font-semibold text-gray-900 flex items-center gap-2"><Download className="w-4 h-4 text-[#6366F1]" />Sauvegarde complète (base + justificatifs)</p>
            <p className="text-xs text-gray-600">Copie intégrale de toutes les données et de tous les fichiers, vérifiée et testée en restauration, à conserver sur votre ordinateur.</p>
            <Button size="sm" onClick={start} disabled={running} className="h-8 text-xs" data-testid="backup-start-btn">{running && <Loader2 className="w-3 h-3 animate-spin mr-1" />}{running ? 'Sauvegarde en cours…' : 'Créer une sauvegarde'}</Button>
            {last && (running || last.statut === 'echec') && <BackupSteps job={last} />}
            {ready && (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 space-y-1" data-testid="backup-ready">
                    <p className="text-xs text-emerald-900 font-semibold">{ready.nom} — {(ready.taille / 1048576).toFixed(1)} Mo — {new Date(ready.termine_le).toLocaleString('fr-FR')}</p>
                    <p className="text-[11px] text-emerald-800 break-all" data-testid="backup-sha">Empreinte : {ready.sha256}</p>
                    <BackupSteps job={ready} />
                    {ready.verification && <BackupVerif v={ready.verification} />}
                    <Button size="sm" onClick={download} className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="backup-download-btn">Télécharger</Button>
                </div>
            )}
            {last?.statut === 'echec' && last !== ready && <p className="text-xs text-red-600" data-testid="backup-error">{last.erreur}</p>}
            <BackupHistory jobs={jobs} />
            <MissingFiles />
            <PublicationLock />
        </div>
    );
}

const HIST = { pret: ['Réussie', 'text-emerald-700'], echec: ['Échouée', 'text-red-600'], en_cours: ['En cours', 'text-amber-600'], expire: ['Expirée (conservation)', 'text-gray-400'] };
const histLabel = j => (j.statut === 'pret' && manquantsNets(j) ? ['Incomplète', 'text-orange-600'] : HIST[j.statut] || [j.statut, 'text-gray-500']);

const ORIG = j => j.declencheur === 'reprise_auto' ? 'Reprise auto' : j.declencheur === 'reprise' ? 'Auto 4h (reprise)' : j.automatique ? 'Auto 2h' : 'Manuelle';
const complOk = j => (j.complement_verification?.resultats || []).filter(r => r.ok).length;
const manquantsNets = j => Math.max(0, (j.verification?.fichiers_manquants || 0) - complOk(j));
const isVerified = j => { const v = j.verification || {}; return j.statut === 'pret' && v.archive_intacte && v.restauration_test?.ok && !manquantsNets(j) && (v.fichiers_identiques || complOk(j) > 0); };
const verifTxt = j => isVerified(j) ? `Oui (restauration testée${complOk(j) ? `, ${complOk(j)} fichier(s) revérifié(s) au démarrage` : ''})` : j.statut === 'pret' ? `Incomplète${manquantsNets(j) ? ` (${manquantsNets(j)} fichier(s) manquant(s))` : ''}` : '—';
const fileCounts = j => {
    const v = j.verification || {};
    return { traites: j.fichiers_faits, total: j.fichiers_total, verifies: j.statut === 'pret' && v.fichiers_dans_archive != null ? v.fichiers_dans_archive + complOk(j) : null,
        manquants: v.fichiers_manquants != null ? manquantsNets(j) : (j.fichiers_erreurs ?? null) };
};
const n = x => (x ?? '—');

function BackupHistory({ jobs: all }) {
    const [more, setMore] = useState(false);
    if (!all.length) return null;
    const jobs = more ? all : all.slice(0, 10);
    return (
        <div data-testid="backup-history">
            <p className="text-xs font-semibold text-gray-700 mt-2 mb-1">Historique des sauvegardes</p>
            <ul className="sm:hidden space-y-1.5" data-testid="backup-history-mobile">
                {jobs.map(j => {
                    const [lbl, cls] = histLabel(j);
                    const c = fileCounts(j);
                    return (
                        <li key={j.id} className="rounded-lg border border-gray-100 p-2 text-[11px]" data-testid={`backup-history-card-${j.id}`}>
                            <div className="flex justify-between gap-2"><span>{new Date(j.at).toLocaleString('fr-FR')}</span><span className={`font-medium ${cls}`}>{lbl}</span></div>
                            <div className="text-gray-500">{ORIG(j)} · {j.taille ? `${(j.taille / 1048576).toFixed(1)} Mo (base seule)` : '—'}</div>
                            <div className="text-gray-600" data-testid={`backup-history-card-counts-${j.id}`}>Traités {n(c.traites)}/{n(c.total)} · Vérifiés <b>{n(c.verifies)}</b> · Manquants <b className={c.manquants ? 'text-red-600' : ''}>{n(c.manquants)}</b></div>
                            <div className={isVerified(j) ? 'text-emerald-700' : 'text-gray-400'}>Vérifiée : {verifTxt(j)}</div>
                            {j.erreur && <div className="text-red-600 break-words">{j.erreur}</div>}
                        </li>
                    );
                })}
            </ul>
            <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-[11px]">
                    <thead><tr className="text-gray-400 text-left"><th className="py-1 pr-2">Date</th><th className="pr-2">Statut</th><th className="pr-2">Origine</th><th className="pr-2 text-right">Traités</th><th className="pr-2 text-right">Vérifiés</th><th className="pr-2 text-right">Manquants</th><th className="pr-2 text-right" title="Base de données + inventaire ; les justificatifs sont copiés à part">Archive</th><th>Vérifiée</th></tr></thead>
                    <tbody>
                        {jobs.map(j => {
                            const [lbl, cls] = histLabel(j);
                            const c = fileCounts(j);
                            return (
                                <tr key={j.id} className="border-t border-gray-100" data-testid={`backup-history-${j.id}`}>
                                    <td className="py-1 pr-2 whitespace-nowrap">{new Date(j.at).toLocaleString('fr-FR')}</td>
                                    <td className={`pr-2 font-medium ${cls}`} title={j.erreur || ''}>{lbl}</td>
                                    <td className="pr-2">{ORIG(j)}</td>
                                    <td className="pr-2 text-right font-mono" data-testid={`backup-history-traites-${j.id}`}>{n(c.traites)}/{n(c.total)}</td>
                                    <td className="pr-2 text-right font-mono" data-testid={`backup-history-verifies-${j.id}`}>{n(c.verifies)}</td>
                                    <td className={`pr-2 text-right font-mono ${c.manquants ? 'text-red-600 font-semibold' : ''}`} data-testid={`backup-history-manquants-${j.id}`}>{n(c.manquants)}</td>
                                    <td className="pr-2 text-right font-mono">{j.taille ? `${(j.taille / 1048576).toFixed(1)} Mo` : '—'}</td>
                                    <td className={isVerified(j) ? 'text-emerald-700' : 'text-gray-400'}>{verifTxt(j)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {all.length > 10 && <button type="button" className="text-[11px] text-[#6366F1] underline mt-1" onClick={() => setMore(m => !m)} data-testid="backup-history-toggle">{more ? 'Voir moins' : `Voir tout (${all.length})`}</button>}
        </div>
    );
}

function MissingFiles() {
    const [d, setD] = useState(null);
    const [motif, setMotif] = useState({});
    const load = () => axios.get(`${API_URL}/api/admin/backup/fichiers-non-sauvegardes`, { withCredentials: true }).then(r => setD(r.data)).catch(() => setD(null));
    useEffect(() => { load(); }, []);
    const list = d?.derniere_sauvegarde?.manquants || [];
    if (!list.length) return null;
    const ack = async (p) => {
        try { await axios.post(`${API_URL}/api/admin/backup/fichiers-perdus`, { chemin: p, motif: motif[p] || '' }, { withCredentials: true }); toast.success('Fichier reconnu perdu : il sera signalé sans bloquer la publication'); load(); }
        catch (e) { toast.error(e.response?.data?.detail || 'Action impossible'); }
    };
    return (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 space-y-2 text-[12px] text-red-900" data-testid="backup-missing-files">
            <p className="font-semibold">Fichiers manquants dans la dernière sauvegarde ({list.length})</p>
            <p>Aucun fichier n'est supprimé. Si l'original et sa copie sont introuvables, le super admin peut le reconnaître comme perdu, avec un motif.</p>
            {list.map(m => (
                <div key={m.chemin} className="rounded-lg bg-white border border-red-100 p-2 space-y-1" data-testid={`backup-missing-${m.chemin}`}>
                    <p className="font-mono break-all">{m.chemin}</p>
                    <p className="text-red-700">{m.erreur}</p>
                    <div className="flex flex-wrap gap-2">
                        <input className="border rounded-md px-2 py-1 flex-1 min-w-0" placeholder="Motif (10 caractères minimum)" value={motif[m.chemin] || ''} onChange={e => setMotif(x => ({ ...x, [m.chemin]: e.target.value }))} data-testid="backup-missing-motif" />
                        <button type="button" className="rounded-md bg-red-600 text-white px-2 py-1 disabled:opacity-50" disabled={(motif[m.chemin] || '').trim().length < 10} onClick={() => ack(m.chemin)} data-testid="backup-missing-ack">Reconnaître perdu</button>
                    </div>
                </div>
            ))}
        </div>
    );
}

function PublicationLock() {
    const [d, setD] = useState(null);
    useEffect(() => { axios.get(`${API_URL}/api/admin/publication/verrou`, { withCredentials: true }).then(r => setD(r.data)).catch(() => setD(null)); }, []);
    if (!d) return null;
    const ok = d.publiable;
    return (
        <div className={`rounded-xl border p-4 space-y-1 text-xs ${ok ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-red-200 bg-red-50 text-red-900'}`} data-testid="publication-lock">
            <p className="text-sm font-semibold" data-testid="publication-lock-status">{ok ? 'Publication possible' : 'Publication bloquée'}</p>
            {ok ? <p>Sauvegarde vérifiée du {new Date(d.termine_le).toLocaleString('fr-FR')} — valable jusqu'à {new Date(d.publiable_jusqu_a).toLocaleTimeString('fr-FR')}.</p>
                : <p>Une nouvelle version refusera de démarrer tant qu'une sauvegarde complète et vérifiée de moins de 2 h n'existe pas : {d.raisons.join(' ; ')}.</p>}
            {!d.verrou_actif && <p className="text-gray-600">Verrou inactif sur la copie de travail (actif en production).</p>}
        </div>
    );
}

function FigesList() {
    const [rows, setRows] = useState(null);
    useEffect(() => { axios.get(`${API_URL}/api/admin/integrity/frais-mutualises-figes`, { withCredentials: true }).then(r => setRows(r.data)).catch(() => setRows([])); }, []);
    if (!rows?.length) return null;
    return (
        <div className="rounded-xl border border-sky-200 bg-sky-50 p-4 space-y-2" data-testid="figes-list">
            <p className="text-sm font-semibold text-sky-900">Frais mutualisés figés ({rows.reduce((n, r) => n + r.nb_frais, 0)})</p>
            <ul className="text-xs text-sky-900 space-y-1">{rows.map(r => <li key={`${r.agency_id}-${r.mois}`} data-testid={`fige-${r.mois}`}><b>{r.mois}</b> — {r.nb_frais} frais, {eur(r.montant_ttc)} € TTC — quote-part figée {eur(r.quote_part_figee)} €{r.non_repartis ? ' — non répartis, à traiter' : ''}</li>)}</ul>
        </div>
    );
}

function CommissionDoubles() {
    const [rows, setRows] = useState(null);
    useEffect(() => { axios.get(`${API_URL}/api/admin/integrity/commission-doublons`, { withCredentials: true }).then(r => setRows(r.data)).catch(() => setRows([])); }, []);
    if (!rows?.length) return null;
    return (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 space-y-2" data-testid="commission-doubles">
            <p className="text-sm font-semibold text-red-900">Véhicules comptés pour plusieurs commerciaux ({rows.length})</p>
            <p className="text-xs text-red-800">Détection uniquement : aucune commission n'est modifiée.</p>
            <ul className="text-xs text-red-900 space-y-1">{rows.map(r => <li key={`${r.mois}-${r.id}`} data-testid={`commission-double-${r.id}`}><b>{r.mois}</b> — {r.immatriculation || r.nom} : {r.commerciaux.map((c, i) => `${c} ${eur(r.commissions[i])} €`).join(' / ')}</li>)}</ul>
        </div>
    );
}

function EcartsList() {
    const [rows, setRows] = useState(null);
    useEffect(() => { axios.get(`${API_URL}/api/admin/integrity/ecarts`, { withCredentials: true }).then(r => setRows(r.data)).catch(() => setRows([])); }, []);
    if (!rows?.length) return null;
    return (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-2" data-testid="ecarts-list">
            <p className="text-sm font-semibold text-amber-900">Écarts facture / dépense à décider ({rows.length})</p>
            <p className="text-xs text-amber-800">Le justificatif est rattaché à une dépense existante dont le montant diffère. Aucun montant n'est modifié automatiquement.</p>
            <ul className="text-xs text-amber-900 space-y-1">{rows.map(r => <li key={r.fv_id} data-testid={`ecart-${r.fv_id}`}><b>{r.immatriculation}</b> — {r.fournisseur} {r.numero_facture} : facture {eur(r.montant_facture)} € / dépense « {r.depense} » {eur(r.montant_depense)} €</li>)}</ul>
        </div>
    );
}

function MergeTool({ dupes, overview, onDone }) {
    const info = (id) => overview.find(a => a.id === id) || {};
    const [plan, setPlan] = useState(null);
    const [confirm, setConfirm] = useState('');
    const [busy, setBusy] = useState(false);
    const [pair, setPair] = useState({ source: '', target: '' });

    const preview = async () => {
        setBusy(true);
        try {
            const r = await axios.post(`${API_URL}/api/admin/agencies/merge`, { source_agency_id: pair.source, target_agency_id: pair.target, dry_run: true }, { withCredentials: true });
            setPlan(r.data);
        } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
        setBusy(false);
    };
    const run = async () => {
        setBusy(true);
        try {
            await axios.post(`${API_URL}/api/admin/agencies/merge`, { source_agency_id: pair.source, target_agency_id: pair.target, dry_run: false, confirm }, { withCredentials: true });
            setPlan(null); toast.success(`Agences réunies et vérifiées — annulation possible dans l'historique`); onDone();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur — aucune modification'); }
        setBusy(false);
    };

    return (
        <div className="rounded-2xl border border-indigo-200 bg-white p-5 space-y-3" data-testid="merge-tool">
            <p className="text-sm font-semibold text-gray-900 flex items-center gap-2"><Merge className="w-4 h-4 text-[#6366F1]" />Réunir deux agences</p>
            {dupes.map(g => (
                <div key={g.nom} className="space-y-2">
                    <p className="text-xs text-gray-600">« {g.nom} » existe {g.ids.length} fois. Choisissez l'agence à conserver :</p>
                    {g.ids.map(id => (
                        <div key={id} className="flex flex-wrap gap-2 items-center">
                            <span className="text-xs text-gray-800 bg-gray-100 rounded px-2 py-1"><span className="font-mono">…{id.slice(-6)}</span> · {info(id).users ?? 0} utilisateurs · {info(id).vehicles ?? 0} véhicules · {info(id).scan_results ?? 0} factures · {info(id).frais_vehicules ?? 0} frais · {info(id).charges ?? 0} charges</span>
                            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => { setPair({ target: id, source: g.ids.find(x => x !== id) }); setPlan(null); }} data-testid={`merge-keep-${id}`}>Conserver celle-ci</Button>
                        </div>
                    ))}
                </div>
            ))}
            {pair.target && (
                <div className="text-xs text-gray-700 space-y-2">
                    <p>Conserver <b className="font-mono">{pair.target}</b> · archiver <b className="font-mono">{pair.source}</b> (jamais supprimée)</p>
                    <Button size="sm" onClick={preview} disabled={busy} className="h-8 text-xs bg-[#6366F1] text-white" data-testid="merge-preview">Simuler (aucune modification)</Button>
                </div>
            )}
            {plan && <MergePlan plan={plan} confirm={confirm} setConfirm={setConfirm} run={run} busy={busy} />}
        </div>
    );
}

export default function CoherencePage() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [overview, setOverview] = useState([]);
    const [merges, setMerges] = useState([]);
    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [c, o, m] = await Promise.all([
                axios.get(`${API_URL}/api/admin/coherence`, { withCredentials: true }),
                axios.get(`${API_URL}/api/admin/agencies/overview`, { withCredentials: true }),
                axios.get(`${API_URL}/api/admin/agencies/merges`, { withCredentials: true }),
            ]);
            setData(c.data); setOverview(o.data); setMerges(m.data);
        }
        catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
        setLoading(false);
    }, []);
    useEffect(() => { load(); }, [load]);
    if (user && user.role !== 'super_admin') return <Navigate to="/dashboard" replace />;

    return (
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-10 space-y-6" data-testid="coherence-page">
            <div className="flex items-center justify-between gap-3">
                <div>
                    <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#6366F1]" />Contrôle de cohérence</h1>
                    <p className="text-xs text-gray-500 mt-1">Lecture seule — aucune donnée n'est modifiée par ce rapport.</p>
                </div>
                <Button variant="outline" onClick={load} disabled={loading} className="h-9 text-xs" data-testid="coherence-refresh">
                    {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}Actualiser
                </Button>
            </div>
            {data && (
                <>
                    <BackupExport />
                    <SystemHealthPanel />
                    <RegularisationPanel />
                    <EcartsList />
                    <RattachementPanel />
                    <CommissionDoubles />
                    <FigesList />
                    {data.agences_meme_nom.length > 0 && <MergeTool key={merges.length} dupes={data.agences_meme_nom} overview={overview} onDone={load} />}
                    <MergeHistory merges={merges} overview={overview} onDone={load} />
                    {data.agences_orphelines.length > 0 && <p className="text-xs text-gray-600" data-testid="coh-orphans">Identifiants d'agence sans fiche : {data.agences_orphelines.join(', ')}</p>}
                    <div className="space-y-2">{Object.keys(LABELS).map(k => data[k] && <Check key={k} k={k} v={data[k]} />)}</div>
                </>
            )}
        </div>
    );
}
